import React, { useMemo } from "react";
import { X, BarChart3, TrendingUp } from "lucide-react";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Pie } from "react-chartjs-2";

ChartJS.register(ArcElement, Tooltip, Legend);

interface ComparisonChartModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: any;
}

const ComparisonChartModal: React.FC<ComparisonChartModalProps> = ({
  isOpen,
  onClose,
  data,
}) => {
  if (!isOpen) return null;

  const result = data?.result || {};
  const comparisonResult = result.comparision_result || {};
  const differences = result.differences || [];

  const formatTypeName = (type: string) =>
    type
      .split("_")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(" ");

  const typeDistribution = useMemo(() => {
    const counts: Record<string, number> = {};
    differences.forEach((diff: any) => {
      const type = diff.difference_type || "Unknown";
      counts[type] = (counts[type] || 0) + 1;
    });
    return counts;
  }, [differences]);

  const chartData = useMemo(() => {
    const entries = Object.entries(typeDistribution);
    const colors = [
      "#6366f1", // Indigo
      "#3b82f6", // Blue
      "#10b981", // Green
      "#f59e0b", // Amber
      "#ef4444", // Red
      "#8b5cf6", // Violet
    ];

    const labels = entries.map(([type]) => formatTypeName(type));
    const dataValues = entries.map(([_, count]) => count);

    return {
      labels,
      datasets: [
        {
          label: "Differences",
          data: dataValues,
          backgroundColor: entries.map((_, i) => colors[i % colors.length]),
          borderColor: "#ffffff",
          borderWidth: 2,
          hoverOffset: 8,
        },
      ],
    };
  }, [typeDistribution]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    cutout: "65%",
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "#1f2937",
        cornerRadius: 6,
        padding: 10,
        titleFont: { size: 14, weight: "bold" },
        bodyFont: { size: 13 },
        callbacks: {
          label: (ctx: any) => {
            const label = ctx.label || "";
            const value = ctx.parsed || 0;
            const total = ctx.dataset.data.reduce(
              (a: number, b: number) => a + b,
              0
            );
            const percent = ((value / total) * 100).toFixed(1);
            return ` ${label}: ${value} (${percent}%)`;
          },
        },
      },
    },
  };

  const legendData = useMemo(() => {
    const entries = Object.entries(typeDistribution);
    const total = entries.reduce((sum, [_, c]) => sum + c, 0);
    const colors = [
      "#6366f1",
      "#3b82f6",
      "#10b981",
      "#f59e0b",
      "#ef4444",
      "#8b5cf6",
    ];
    return entries.map(([type, count], i) => ({
      type,
      count,
      percentage: ((count / total) * 100).toFixed(1),
      color: colors[i % colors.length],
    }));
  }, [typeDistribution]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-indigo-600 rounded-lg">
              <BarChart3 className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Comparison Overview
              </h2>
              <p className="text-sm text-gray-500">
                Document comparison statistics
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-200 transition"
            title="Close"
          >
            <X className="h-5 w-5 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Chart Section */}
            <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
              <h3 className="text-sm font-medium text-gray-700 mb-4 flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-indigo-600" />
                Type Distribution
              </h3>
              <div className="flex justify-center items-center mb-6">
                <div className="relative w-[280px] h-[280px]">
                  <Pie data={chartData} options={chartOptions as any} />
                  <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                    <div className="text-4xl font-bold text-gray-900">
                      {differences.length}
                    </div>
                    <span className="text-xs text-gray-500 uppercase tracking-wide">
                      Total Changes
                    </span>
                  </div>
                </div>
              </div>

              {/* Legend */}
              <div className="space-y-2">
                {legendData.map((item, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between border border-gray-100 rounded-lg p-2 hover:bg-gray-50 transition"
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className="inline-block w-3 h-3 rounded-sm"
                        style={{ backgroundColor: item.color }}
                      />
                      <span className="text-sm text-gray-800 font-medium">
                        {formatTypeName(item.type)}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600 flex items-center gap-3">
                      <span className="font-semibold">{item.count}</span>
                      <span className="text-xs text-gray-500">
                        {item.percentage}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Stats Section */}
            <div className="space-y-4">
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-5">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">
                  Comparison Summary
                </h3>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">
                    Similarity Score
                  </span>
                  <span className="text-xl font-bold text-gray-900">
                    {comparisonResult.Similarity || 0}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-indigo-600 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${comparisonResult.Similarity || 0}%` }}
                  />
                </div>
              </div>

              {/* Metrics */}
              <div className="grid grid-cols-2 gap-4">
                {[
                  {
                    label: "Words Compared",
                    value: comparisonResult.words_compared,
                  },
                  {
                    label: "Total Changes",
                    value: comparisonResult.total_changes,
                  },
                  {
                    label: "Text Omissions",
                    value: comparisonResult.text_omissions,
                  },
                  { label: "New Texts", value: comparisonResult.new_texts },
                  {
                    label: "Spelling Errors",
                    value: comparisonResult.spelling_errors,
                  },
                  {
                    label: "Context Changes",
                    value: comparisonResult.context_changes,
                  },
                  {
                    label: "Content Errors",
                    value: comparisonResult.content_errors,
                  },
                  {
                    label: "Text Swapped",
                    value: comparisonResult.text_swapped,
                  },
                ].map((item, i) => (
                  <div
                    key={i}
                    className="bg-white border border-gray-200 rounded-xl p-3 shadow-sm"
                  >
                    <div className="text-xs text-gray-500 font-medium mb-1">
                      {item.label}
                    </div>
                    <div className="text-lg font-bold text-gray-800">
                      {item.value || 0}
                    </div>
                  </div>
                ))}
              </div>

              {/* Summary */}
              {result.comparison_summary && (
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                  <div className="text-sm font-semibold text-gray-700 mb-1">
                    Summary
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {result.comparison_summary}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-6 py-2.5 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ComparisonChartModal;
